// client/src/components/pages/Geofencing/zoneEditor/ZoneEditorMap.tsx
// The zone editor's map surface (ST-00239): MapLibre via the shared LexiStarMap,
// with every drawing and editing interaction built from plain map events and
// draggable DOM handles (react-map-gl Markers). It turns pointer input into
// zoneEditorState actions and renders the state; it owns no geometry rules.
//
//   Drawing   polygon: click / tap corners, close on the first corner, a
//             double-click / double-tap or Enter; rectangle and circle: press and
//             drag (or click twice), with live sizes next to the cursor.
//   Editing   drag corners; drag the small edge dots to add a corner; click a
//             corner then Delete, or right-click / press-and-hold it for a menu;
//             drag the move handle to move the whole zone; circles move by their
//             centre and resize by their edge handle.
//   Context   the tenant's other zones drawn faintly (names on hover), snapping
//             to their corners and edges (hold Alt to switch it off for a moment),
//             drone overlays, self-intersection marks.
import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { Dispatch, MouseEvent as ReactMouseEvent, ReactNode } from 'react';
import { Layer, Marker, Source } from 'react-map-gl/maplibre';
import type {
  MapLayerMouseEvent,
  MapLayerTouchEvent,
  MarkerDragEvent,
} from 'react-map-gl/maplibre';
import type { Map as MaplibreMap } from 'maplibre-gl';
import type { Feature, FeatureCollection } from 'geojson';
import { Move } from 'lucide-react';
import { LexiStarMap } from '../../../../types/commonComponents';
import {
  edgeMidpoints,
  formatLength,
  haversineM,
  insertVertex,
  labelPoint,
  moveVertex,
  rectangleRing,
  ringBounds,
  shapeRing,
  snapPoint,
  translateShape,
  destination,
  circleRing,
  MAX_RADIUS_M,
  type Bounds,
  type LngLat,
  type SnapResult,
  type SnapSource,
  type ZoneShape,
} from './zoneGeometry';
import type { EditorAction, EditorState } from './zoneEditorState';
import './zoneEditor.css';

const TEAL = '#14b8a6';
const RED = '#ef4444';

/** Another zone of the tenant, drawn faintly for context and snapped to. */
export interface ContextZone {
  id: number;
  name: string;
  shape: ZoneShape;
  color: string;
}

/** An aerial (drone) overlay row as the aerial-overlays API returns it. */
export interface EditorOverlay {
  overlay_id: number | string;
  file_path: string;
  bounds_west: number;
  bounds_east: number;
  bounds_north: number;
  bounds_south: number;
  opacity?: number | null;
}

export interface ZoneEditorMapHandle {
  getMap: () => MaplibreMap | null;
  fitBounds: (bounds: Bounds, maxZoom?: number) => void;
  flyTo: (p: LngLat, zoom?: number) => void;
}

export interface ZoneEditorMapProps {
  state: EditorState;
  dispatch: Dispatch<EditorAction>;
  otherZones: ContextZone[];
  overlays: EditorOverlay[];
  overlayBase: string;
  showOverlays: boolean;
  showOtherZones: boolean;
  basemap: 'satellite' | 'street';
  snapping: boolean;
  touch: boolean;
  /** Self-intersection points to mark; non-empty also turns the outline red. */
  crossings: LngLat[];
  invalid: boolean;
  initialViewState: { longitude: number; latitude: number; zoom: number };
  searchPin: LngLat | null;
  /** Called once with the map (for the parent's zoom-to / search). */
  onReady?: (map: MaplibreMap) => void;
  children?: ReactNode;
}

type Px = [number, number];

interface DragState {
  kind: 'vertex' | 'mid' | 'move' | 'center' | 'radius';
  index: number;
  base: ZoneShape;
  from: LngLat;
  pos: LngLat;
}

interface MenuState {
  x: number;
  y: number;
  index: number;
}

const toLngLat = (ll: { lng: number; lat: number }): LngLat => [ll.lng, ll.lat];

const featureCollection = (features: Feature[]): FeatureCollection => ({
  type: 'FeatureCollection',
  features,
});

const polygonFeature = (
  ring: readonly LngLat[],
  properties: Record<string, unknown> = {}
): Feature => ({
  type: 'Feature',
  properties,
  geometry: { type: 'Polygon', coordinates: [[...ring, ring[0]!]] },
});

const lineFeature = (
  coords: readonly LngLat[],
  properties: Record<string, unknown> = {}
): Feature => ({
  type: 'Feature',
  properties,
  geometry: { type: 'LineString', coordinates: coords.map((c) => [c[0], c[1]]) },
});

/**
 * Room around a framed zone: clear of the search box, tool bar and hint bar,
 * with a margin so neighbouring zones stay in view.
 */
export function framePadding(map: MaplibreMap): {
  top: number;
  bottom: number;
  left: number;
  right: number;
} {
  const c = map.getContainer();
  const small = c.clientWidth < 640 || c.clientHeight < 480;
  return small
    ? { top: 64, bottom: 72, left: 60, right: 28 }
    : { top: 110, bottom: 130, left: 110, right: 80 };
}

/** Ignore the mouse events a browser emulates right after a touch. */
const TOUCH_GHOST_MS = 800;
/** Long press on a corner opens its menu (iOS has no contextmenu event). */
const LONG_PRESS_MS = 550;

// ---------- handles ----------

interface HandleProps {
  className: string;
  label: string;
  title?: string;
  pressed?: boolean;
  children?: ReactNode;
  onContext?: (x: number, y: number) => void;
}

/**
 * A handle button inside a Marker. Right-click and press-and-hold call
 * `onContext` with page coordinates; native listeners stop those events before
 * they reach the map (React's root listener would be too late).
 */
const HandleButton = memo(function HandleButton({
  className,
  label,
  title,
  pressed,
  children,
  onContext,
}: HandleProps) {
  const ref = useRef<HTMLButtonElement>(null);
  const ctxRef = useRef(onContext);
  ctxRef.current = onContext;
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    let timer: number | null = null;
    let start: Px | null = null;
    const clear = (): void => {
      if (timer != null) window.clearTimeout(timer);
      timer = null;
      start = null;
    };
    const onContextMenu = (e: MouseEvent): void => {
      e.preventDefault();
      e.stopPropagation();
      if (ctxRef.current) ctxRef.current(e.clientX, e.clientY);
    };
    const onDown = (e: PointerEvent): void => {
      if (e.pointerType !== 'touch' || !ctxRef.current) return;
      start = [e.clientX, e.clientY];
      const { clientX, clientY } = e;
      timer = window.setTimeout(() => {
        timer = null;
        el.dataset.longPress = '1';
        if (ctxRef.current) ctxRef.current(clientX, clientY);
      }, LONG_PRESS_MS);
    };
    const onMove = (e: PointerEvent): void => {
      if (start && Math.hypot(e.clientX - start[0], e.clientY - start[1]) > 8) clear();
    };
    const onDblClick = (e: MouseEvent): void => e.stopPropagation();
    el.addEventListener('contextmenu', onContextMenu);
    el.addEventListener('pointerdown', onDown);
    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerup', clear);
    el.addEventListener('pointercancel', clear);
    el.addEventListener('dblclick', onDblClick);
    return () => {
      clear();
      el.removeEventListener('contextmenu', onContextMenu);
      el.removeEventListener('pointerdown', onDown);
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerup', clear);
      el.removeEventListener('pointercancel', clear);
      el.removeEventListener('dblclick', onDblClick);
    };
  }, []);
  return (
    <button
      ref={ref}
      type="button"
      className={`ze-handle ${className}`}
      aria-label={label}
      title={title ?? label}
      aria-pressed={pressed}
      // Map keyboard shortcuts live on the editor; keep focus clicks from scrolling.
      onMouseDown={(e: ReactMouseEvent) => e.preventDefault()}
    >
      <span className="ze-dot">{children}</span>
    </button>
  );
});

/** True (and cleared) when a long press just opened a menu on this handle. */
function consumeLongPress(e: { originalEvent: MouseEvent }): boolean {
  const el = (e.originalEvent.target as HTMLElement | null)?.closest?.('.ze-handle') as
    HTMLElement | null | undefined;
  if (el?.dataset.longPress) {
    delete el.dataset.longPress;
    return true;
  }
  return false;
}

// ---------- the map ----------

const ZoneEditorMap = forwardRef<ZoneEditorMapHandle, ZoneEditorMapProps>(function ZoneEditorMap(
  {
    state,
    dispatch,
    otherZones,
    overlays,
    overlayBase,
    showOverlays,
    showOtherZones,
    basemap,
    snapping,
    touch,
    crossings,
    invalid,
    initialViewState,
    searchPin,
    onReady,
    children,
  },
  ref
) {
  const mapRef = useRef<MaplibreMap | null>(null);
  const [viewTick, setViewTick] = useState(0);
  const [pointer, setPointer] = useState<{ lngLat: LngLat; px: Px } | null>(null);
  const [snap, setSnap] = useState<SnapResult | null>(null);
  const [hover, setHover] = useState<{ name: string; px: Px } | null>(null);
  const [drag, setDrag] = useState<DragState | null>(null);
  const [menu, setMenu] = useState<MenuState | null>(null);
  const altHeld = useRef(false);
  const lastTouchAt = useRef(0);
  const shapeDrag = useRef<{ startPx: Px; moved: boolean; second: boolean } | null>(null);
  const stateRef = useRef(state);
  stateRef.current = state;

  const { tool, draft, selected } = state;
  const view = state.preview ?? state.shape;

  useImperativeHandle(
    ref,
    () => ({
      getMap: () => mapRef.current,
      fitBounds: (b, maxZoom = 18) => {
        const map = mapRef.current;
        if (!map) return;
        const same = b[0][0] === b[1][0] && b[0][1] === b[1][1];
        if (same) map.flyTo({ center: b[0], zoom: Math.min(maxZoom, 17) });
        else map.fitBounds(b, { padding: framePadding(map), maxZoom, duration: 600 });
      },
      flyTo: (p, zoom) => {
        mapRef.current?.flyTo({ center: p, zoom: zoom ?? Math.max(mapRef.current.getZoom(), 16) });
      },
    }),
    []
  );

  // Alt held = snapping off for the moment (also read from mouse events directly).
  useEffect(() => {
    const down = (e: KeyboardEvent): void => {
      if (e.key === 'Alt') altHeld.current = true;
    };
    const up = (e: KeyboardEvent): void => {
      if (e.key === 'Alt') altHeld.current = false;
    };
    const blur = (): void => {
      altHeld.current = false;
    };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    window.addEventListener('blur', blur);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
      window.removeEventListener('blur', blur);
    };
  }, []);

  // Close the corner menu on any outside press or Escape.
  useEffect(() => {
    if (!menu) return undefined;
    const close = (e: Event): void => {
      if ((e.target as HTMLElement | null)?.closest?.('[data-ze-menu]')) return;
      setMenu(null);
    };
    const esc = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setMenu(null);
    };
    window.addEventListener('pointerdown', close, true);
    window.addEventListener('keydown', esc);
    return () => {
      window.removeEventListener('pointerdown', close, true);
      window.removeEventListener('keydown', esc);
    };
  }, [menu]);

  // Leaving a tool drops the rubber band and the snap ring.
  useEffect(() => {
    setPointer(null);
    setSnap(null);
    shapeDrag.current = null;
  }, [tool]);

  // ---------- snapping ----------

  const snapSources = useMemo(() => {
    return otherZones.map((z) => {
      const outline = shapeRing(z.shape, 64);
      const src: SnapSource & { bounds: Bounds | null } = {
        label: z.name,
        vertices: z.shape.kind === 'polygon' ? z.shape.ring : [],
        outline,
        bounds: ringBounds(outline),
      };
      return src;
    });
  }, [otherZones]);

  const snapAt = useCallback(
    (px: Px, raw: LngLat, altKey: boolean): { point: LngLat; snap: SnapResult | null } => {
      const map = mapRef.current;
      if (!map || !snapping || altKey || altHeld.current || !showOtherZones) {
        return { point: raw, snap: null };
      }
      const vb = map.getBounds();
      const pad = (vb.getEast() - vb.getWest()) * 0.05;
      const near = snapSources.filter(
        (s) =>
          s.bounds &&
          s.bounds[0][0] <= vb.getEast() + pad &&
          s.bounds[1][0] >= vb.getWest() - pad &&
          s.bounds[0][1] <= vb.getNorth() + pad &&
          s.bounds[1][1] >= vb.getSouth() - pad
      );
      if (!near.length) return { point: raw, snap: null };
      const project = (p: LngLat): Px => {
        const q = map.project(p);
        return [q.x, q.y];
      };
      const unproject = (q: Px): LngLat => toLngLat(map.unproject(q));
      const s = snapPoint(px, near, project, unproject, {
        vertexPx: touch ? 22 : 14,
        edgePx: touch ? 14 : 9,
      });
      return { point: s ? s.point : raw, snap: s };
    },
    [snapping, snapSources, touch, showOtherZones]
  );

  const projectPx = useCallback((p: LngLat): Px | null => {
    const map = mapRef.current;
    if (!map) return null;
    const q = map.project(p);
    return [q.x, q.y];
  }, []);

  // ---------- map events: drawing ----------

  const isGhostMouse = (): boolean => Date.now() - lastTouchAt.current < TOUCH_GHOST_MS;

  const handleClick = useCallback(
    (e: MapLayerMouseEvent) => {
      const s = stateRef.current;
      if (s.tool === 'edit') {
        if (s.selected != null) dispatch({ type: 'select', index: null });
        const f = e.features?.[0];
        if (f && touch) {
          setHover({ name: String(f.properties?.name ?? ''), px: [e.point.x, e.point.y] });
          window.setTimeout(() => setHover(null), 2200);
        }
        return;
      }
      if (s.tool !== 'polygon') return;
      const pts = s.draft?.kind === 'polygon' ? s.draft.points : [];
      const tol = touch ? 24 : 10;
      const px: Px = [e.point.x, e.point.y];
      const near = (p: LngLat | undefined): boolean => {
        if (!p) return false;
        const q = projectPx(p);
        return !!q && Math.hypot(q[0] - px[0], q[1] - px[1]) <= tol;
      };
      if (pts.length >= 3 && near(pts[0])) {
        dispatch({ type: 'finishDraft' });
        return;
      }
      if (near(pts[pts.length - 1])) {
        // Second click of a double-click (or a double tap) on the last corner.
        if (pts.length >= 3) dispatch({ type: 'finishDraft' });
        return;
      }
      const { point } = snapAt(px, toLngLat(e.lngLat), e.originalEvent.altKey);
      dispatch({ type: 'draftPoint', point });
    },
    [dispatch, projectPx, snapAt, touch]
  );

  const beginShapeDraw = useCallback(
    (px: Px, raw: LngLat, altKey: boolean) => {
      const s = stateRef.current;
      if (s.tool !== 'rectangle' && s.tool !== 'circle') return;
      const { point } = snapAt(px, raw, altKey);
      if (s.draft && (s.draft.kind === 'rectangle' || s.draft.kind === 'circle')) {
        // Second click of the click-click gesture.
        shapeDrag.current = { startPx: px, moved: false, second: true };
        return;
      }
      shapeDrag.current = { startPx: px, moved: false, second: false };
      dispatch({
        type: 'draftStart',
        draft:
          s.tool === 'rectangle'
            ? { kind: 'rectangle', start: point, end: null }
            : { kind: 'circle', center: point, edge: null },
      });
    },
    [dispatch, snapAt]
  );

  const moveShapeDraw = useCallback(
    (px: Px, raw: LngLat, altKey: boolean) => {
      const s = stateRef.current;
      if (!s.draft || s.draft.kind === 'polygon') return;
      const sd = shapeDrag.current;
      if (sd && !sd.moved && Math.hypot(px[0] - sd.startPx[0], px[1] - sd.startPx[1]) > 4)
        sd.moved = true;
      // Circles snap their edge too (a stockpile edge to a neighbour's corner).
      const { point, snap: sn } = snapAt(px, raw, altKey);
      setSnap(sn);
      dispatch({ type: 'draftMove', point });
    },
    [dispatch, snapAt]
  );

  const endShapeDraw = useCallback(
    (px: Px, raw: LngLat, altKey: boolean) => {
      const sd = shapeDrag.current;
      shapeDrag.current = null;
      if (!sd) return;
      if (sd.second || sd.moved) {
        const { point } = snapAt(px, raw, altKey);
        dispatch({ type: 'draftMove', point });
        dispatch({ type: 'finishDraft' });
        setSnap(null);
      }
      // else: a plain click — wait for the second click (click-click gesture).
    },
    [dispatch, snapAt]
  );

  const handleMouseDown = useCallback(
    (e: MapLayerMouseEvent) => {
      if (isGhostMouse() || e.originalEvent.button !== 0) return;
      beginShapeDraw([e.point.x, e.point.y], toLngLat(e.lngLat), e.originalEvent.altKey);
    },
    [beginShapeDraw]
  );

  const handleMouseMove = useCallback(
    (e: MapLayerMouseEvent) => {
      if (isGhostMouse()) return;
      const s = stateRef.current;
      const px: Px = [e.point.x, e.point.y];
      const raw = toLngLat(e.lngLat);
      // Other zones: name on hover (edit mode and while drawing).
      const f = e.features?.[0];
      setHover((h) => {
        if (!f) return h ? null : h;
        const name = String(f.properties?.name ?? '');
        return { name, px };
      });
      if (s.tool === 'polygon') {
        const { point, snap: sn } = snapAt(px, raw, e.originalEvent.altKey);
        setSnap(sn);
        setPointer({ lngLat: point, px });
      } else if (s.tool === 'rectangle' || s.tool === 'circle') {
        if (s.draft) moveShapeDraw(px, raw, e.originalEvent.altKey);
        else {
          const { point, snap: sn } = snapAt(px, raw, e.originalEvent.altKey);
          setSnap(sn);
          setPointer({ lngLat: point, px });
        }
      }
    },
    [moveShapeDraw, snapAt]
  );

  const handleMouseUp = useCallback(
    (e: MapLayerMouseEvent) => {
      if (isGhostMouse()) return;
      endShapeDraw([e.point.x, e.point.y], toLngLat(e.lngLat), e.originalEvent.altKey);
    },
    [endShapeDraw]
  );

  const handleMouseLeave = useCallback(() => {
    setHover(null);
    if (!shapeDrag.current) setPointer(null);
  }, []);

  const handleTouchStart = useCallback(
    (e: MapLayerTouchEvent) => {
      lastTouchAt.current = Date.now();
      if (e.points.length !== 1) {
        // A second finger: this is a pinch, not a drawing gesture.
        const sd = shapeDrag.current;
        if (sd && !sd.second && !sd.moved) dispatch({ type: 'clearDraft' });
        shapeDrag.current = null;
        return;
      }
      beginShapeDraw([e.point.x, e.point.y], toLngLat(e.lngLat), false);
    },
    [beginShapeDraw, dispatch]
  );

  const handleTouchMove = useCallback(
    (e: MapLayerTouchEvent) => {
      lastTouchAt.current = Date.now();
      if (e.points.length !== 1 || !shapeDrag.current) return;
      moveShapeDraw([e.point.x, e.point.y], toLngLat(e.lngLat), false);
    },
    [moveShapeDraw]
  );

  const handleTouchEnd = useCallback(
    (e: MapLayerTouchEvent) => {
      lastTouchAt.current = Date.now();
      endShapeDraw([e.point.x, e.point.y], toLngLat(e.lngLat), false);
    },
    [endShapeDraw]
  );

  // ---------- handle drags (edit mode) ----------

  const dragRef = useRef<DragState | null>(null);
  const lastPreview = useRef<ZoneShape | null>(null);

  const startDrag = useCallback(
    (kind: DragState['kind'], index: number, e: MarkerDragEvent) => {
      const base = stateRef.current.shape;
      if (!base) return;
      setMenu(null);
      const p = toLngLat(e.lngLat);
      const d: DragState = { kind, index, base, from: p, pos: p };
      dragRef.current = d;
      lastPreview.current = null;
      setDrag(d);
      if (kind === 'vertex') dispatch({ type: 'select', index });
    },
    [dispatch]
  );

  const dragTo = useCallback(
    (e: MarkerDragEvent): void => {
      const d = dragRef.current;
      if (!d) return;
      const raw = toLngLat(e.lngLat);
      let pos = raw;
      if (d.kind === 'vertex' || d.kind === 'mid' || d.kind === 'radius') {
        const px = projectPx(raw);
        if (px) {
          const r = snapAt(px, raw, false);
          pos = r.point;
          setSnap(r.snap);
        }
      }
      let next: ZoneShape | null = null;
      const base = d.base;
      if (d.kind === 'vertex' && base.kind === 'polygon') {
        next = { kind: 'polygon', ring: moveVertex(base.ring, d.index, pos) };
      } else if (d.kind === 'mid' && base.kind === 'polygon') {
        next = { kind: 'polygon', ring: insertVertex(base.ring, d.index, pos) };
      } else if (d.kind === 'move' || d.kind === 'center') {
        next = translateShape(base, d.from, pos);
      } else if (d.kind === 'radius' && base.kind === 'circle') {
        const r = Math.min(MAX_RADIUS_M, Math.max(1, Math.round(haversineM(base.center, pos))));
        next = { ...base, radiusM: r };
      }
      const nd = { ...d, pos };
      dragRef.current = nd;
      setDrag(nd);
      if (next) {
        lastPreview.current = next;
        dispatch({ type: 'preview', shape: next });
      }
    },
    [dispatch, projectPx, snapAt]
  );

  const endDrag = useCallback((): void => {
    const d = dragRef.current;
    const next = lastPreview.current;
    dragRef.current = null;
    lastPreview.current = null;
    setDrag(null);
    setSnap(null);
    if (d && next) {
      const label =
        d.kind === 'vertex'
          ? 'Move corner'
          : d.kind === 'mid'
            ? 'Add corner'
            : d.kind === 'radius'
              ? 'Resize circle'
              : 'Move zone';
      dispatch({ type: 'commit', shape: next, label });
      if (d.kind === 'mid') dispatch({ type: 'select', index: d.index + 1 });
    } else {
      dispatch({ type: 'preview', shape: null });
    }
  }, [dispatch]);

  // A drag released over the tool bar or hint bar never reaches the map, which
  // would leave MapLibre's marker drag running: end it ourselves.
  useEffect(() => {
    if (!drag) return undefined;
    const up = (e: PointerEvent): void => {
      const map = mapRef.current;
      if (!map || map.getCanvasContainer().contains(e.target as Node)) return;
      window.setTimeout(() => {
        if (dragRef.current) {
          map.fire('mouseup');
          map.fire('touchend');
        }
      }, 0);
    };
    window.addEventListener('pointerup', up, true);
    return () => window.removeEventListener('pointerup', up, true);
  }, [drag]);

  // ---------- derived render data ----------

  const othersData = useMemo(
    () =>
      featureCollection(
        otherZones.map((z) =>
          polygonFeature(shapeRing(z.shape, 64), { id: z.id, name: z.name, color: z.color })
        )
      ),
    [otherZones]
  );

  // The outline is a separate LineString: geojson-vt drops polygon rings whose net
  // area is ~0 (a symmetric bow-tie), and a crossing outline must stay visible.
  const shapeData = useMemo(() => {
    if (!view) return featureCollection([]);
    const ring = shapeRing(view, 128);
    return featureCollection([
      polygonFeature(ring, { role: 'fill' }),
      lineFeature([...ring, ring[0]!], { role: 'outline' }),
    ]);
  }, [view]);

  const drawing = tool !== 'edit';

  const draftData = useMemo(() => {
    const features: Feature[] = [];
    const cursor = pointer?.lngLat ?? null;
    if (draft?.kind === 'polygon') {
      const pts = draft.points;
      if (pts.length >= 2) features.push(lineFeature(pts, { role: 'edge' }));
      if (cursor && pts.length && !touch) {
        const band: LngLat[] = [pts[pts.length - 1]!, cursor];
        if (pts.length >= 2) band.push(pts[0]!);
        features.push(lineFeature(band, { role: 'band' }));
      }
      const fillPts = cursor && !touch ? [...pts, cursor] : pts;
      if (fillPts.length >= 3) features.push(polygonFeature(fillPts, { role: 'fill' }));
    } else if (draft?.kind === 'rectangle') {
      const end = draft.end ?? cursor;
      if (end) features.push(polygonFeature(rectangleRing(draft.start, end), { role: 'fill' }));
    } else if (draft?.kind === 'circle') {
      const edge = draft.edge ?? cursor;
      if (edge) {
        const r = haversineM(draft.center, edge);
        if (r > 0) {
          features.push(polygonFeature(circleRing(draft.center, r, 96), { role: 'fill' }));
          features.push(lineFeature([draft.center, edge], { role: 'band' }));
        }
      }
    }
    return featureCollection(features);
  }, [draft, pointer, touch]);

  // Other zones' names, once zoomed in enough to read them (at most 60 on screen).
  const otherLabelPoints = useMemo(
    () => otherZones.map((z) => ({ id: z.id, name: z.name, at: labelPoint(z.shape) })),
    [otherZones]
  );
  const otherLabels = useMemo(() => {
    const map = mapRef.current;
    if (!map || !showOtherZones || map.getZoom() < 14.5) return [];
    const vb = map.getBounds();
    return otherLabelPoints
      .filter(
        (l) =>
          l.at[0] >= vb.getWest() &&
          l.at[0] <= vb.getEast() &&
          l.at[1] >= vb.getSouth() &&
          l.at[1] <= vb.getNorth()
      )
      .slice(0, 60);
    // viewTick: recompute after the map moves or zooms.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otherLabelPoints, showOtherZones, viewTick]);

  // Which edges are long enough on screen to carry an "add corner" dot.
  const midpointVisible = useMemo(() => {
    const map = mapRef.current;
    if (!map || !view || view.kind !== 'polygon') return [] as boolean[];
    const ring = view.ring;
    return ring.map((p, i) => {
      const a = map.project(p);
      const b = map.project(ring[(i + 1) % ring.length]!);
      return Math.hypot(a.x - b.x, a.y - b.y) >= (touch ? 56 : 36);
    });
    // viewTick: recompute after the map moves or zooms.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, viewTick, touch]);

  // Live size next to the cursor while drawing.
  const drawTip = useMemo((): { text: string; px: Px } | null => {
    if (!pointer && !drag) return null;
    if (drag?.kind === 'radius' && view?.kind === 'circle') {
      const px = projectPx(drag.pos);
      return px ? { text: `Radius ${formatLength(view.radiusM)}`, px } : null;
    }
    if (!pointer) return null;
    if (draft?.kind === 'polygon' && draft.points.length) {
      const last = draft.points[draft.points.length - 1]!;
      return { text: formatLength(haversineM(last, pointer.lngLat)), px: pointer.px };
    }
    if (draft?.kind === 'rectangle') {
      const end = draft.end ?? pointer.lngLat;
      const w = haversineM(draft.start, [end[0], draft.start[1]]);
      const h = haversineM(draft.start, [draft.start[0], end[1]]);
      return { text: `${formatLength(w)} × ${formatLength(h)}`, px: pointer.px };
    }
    if (draft?.kind === 'circle') {
      const edge = draft.edge ?? pointer.lngLat;
      return { text: `Radius ${formatLength(haversineM(draft.center, edge))}`, px: pointer.px };
    }
    return null;
  }, [pointer, drag, draft, view, projectPx]);

  // Touch drawing has no pointer: anchor the size readout at the gesture instead.
  const touchTip = useMemo((): { text: string; at: LngLat } | null => {
    if (!touch || !draft) return null;
    if (draft.kind === 'rectangle' && draft.end) {
      const w = haversineM(draft.start, [draft.end[0], draft.start[1]]);
      const h = haversineM(draft.start, [draft.start[0], draft.end[1]]);
      return { text: `${formatLength(w)} × ${formatLength(h)}`, at: draft.end };
    }
    if (draft.kind === 'circle' && draft.edge) {
      return {
        text: `Radius ${formatLength(haversineM(draft.center, draft.edge))}`,
        at: draft.edge,
      };
    }
    return null;
  }, [draft, touch]);

  const handleLoad = useCallback(
    (e: { target: MaplibreMap }) => {
      mapRef.current = e.target;
      e.target.on('moveend', () => setViewTick((t) => t + 1));
      setViewTick((t) => t + 1);
      onReady?.(e.target);
    },
    [onReady]
  );

  const ghost = drawing && !!state.shape; // old shape faded while a replacement is drawn
  const shapeColor = invalid ? RED : TEAL;

  const polygon = view?.kind === 'polygon' ? view : null;
  const circle = view?.kind === 'circle' ? view : null;
  const editing = tool === 'edit' && !!view;
  const handlesHidden = drag?.kind === 'move' || drag?.kind === 'center';
  const handleRing: LngLat[] =
    drag?.kind === 'mid' && drag.base.kind === 'polygon' ? drag.base.ring : (polygon?.ring ?? []);
  const vertexCount = state.shape?.kind === 'polygon' ? state.shape.ring.length : 0;

  return (
    <div
      className={`absolute inset-0 ${tool === 'rectangle' || tool === 'circle' ? 'ze-map-drawing' : ''}`}
    >
      <LexiStarMap
        className="absolute inset-0"
        initialViewState={initialViewState}
        maxZoom={22}
        basemap={basemap}
        showBasemapToggle={false}
        navigationPosition="bottom-right"
        onLoad={handleLoad}
        onClick={handleClick}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        interactiveLayerIds={showOtherZones ? ['ze-others-fill'] : []}
        cursor={drawing ? 'crosshair' : drag ? 'grabbing' : undefined}
        doubleClickZoom={false}
        dragPan={tool === 'edit' || tool === 'polygon'}
        boxZoom={false}
      >
        {/* Drone overlays (drawing context) */}
        {showOverlays &&
          overlays.map((ov) => (
            <Source
              key={ov.overlay_id}
              id={`aerial-${ov.overlay_id}`}
              type="image"
              url={`${overlayBase}${ov.file_path}`}
              coordinates={[
                [ov.bounds_west, ov.bounds_north],
                [ov.bounds_east, ov.bounds_north],
                [ov.bounds_east, ov.bounds_south],
                [ov.bounds_west, ov.bounds_south],
              ]}
            >
              <Layer
                id={`aerial-layer-${ov.overlay_id}`}
                type="raster"
                paint={{ 'raster-opacity': ov.opacity ?? 0.8, 'raster-fade-duration': 0 }}
              />
            </Source>
          ))}

        {/* The tenant's other zones, faint */}
        {showOtherZones && (
          <Source id="ze-others" type="geojson" data={othersData}>
            <Layer
              id="ze-others-fill"
              type="fill"
              paint={{ 'fill-color': ['get', 'color'], 'fill-opacity': 0.14 }}
            />
            <Layer
              id="ze-others-casing"
              type="line"
              paint={{ 'line-color': '#ffffff', 'line-width': 3, 'line-opacity': 0.5 }}
            />
            <Layer
              id="ze-others-line"
              type="line"
              paint={{
                'line-color': ['get', 'color'],
                'line-width': 1.75,
                'line-opacity': 0.95,
                'line-dasharray': [3, 2],
              }}
            />
            <Layer
              id="ze-others-hover"
              type="line"
              filter={['==', ['get', 'name'], hover?.name ?? '']}
              paint={{ 'line-color': ['get', 'color'], 'line-width': 3.5 }}
            />
          </Source>
        )}

        {otherLabels.map((l) => (
          <Marker key={`ol${l.id}`} longitude={l.at[0]} latitude={l.at[1]}>
            <span className="ze-zone-label">{l.name}</span>
          </Marker>
        ))}

        {/* This zone */}
        <Source id="ze-shape" type="geojson" data={shapeData}>
          <Layer
            id="ze-shape-fill"
            type="fill"
            filter={['==', ['get', 'role'], 'fill']}
            paint={{ 'fill-color': shapeColor, 'fill-opacity': ghost ? 0.08 : 0.2 }}
          />
          <Layer
            id="ze-shape-casing"
            type="line"
            filter={['==', ['get', 'role'], 'outline']}
            paint={{
              'line-color': '#ffffff',
              'line-width': ghost ? 3 : 6,
              'line-opacity': ghost ? 0.4 : 0.85,
            }}
          />
          <Layer
            id="ze-shape-line"
            type="line"
            filter={['==', ['get', 'role'], 'outline']}
            paint={{
              'line-color': shapeColor,
              'line-width': ghost ? 1.5 : 3,
              'line-opacity': ghost ? 0.6 : 1,
              ...(ghost ? { 'line-dasharray': [2, 2] } : {}),
            }}
          />
        </Source>

        {/* Shape being drawn */}
        <Source id="ze-draft" type="geojson" data={draftData}>
          <Layer
            id="ze-draft-fill"
            type="fill"
            filter={['==', ['get', 'role'], 'fill']}
            paint={{ 'fill-color': TEAL, 'fill-opacity': 0.16 }}
          />
          <Layer
            id="ze-draft-fill-outline-casing"
            type="line"
            filter={['==', ['get', 'role'], 'fill']}
            paint={{ 'line-color': '#ffffff', 'line-width': 5, 'line-opacity': 0.7 }}
          />
          <Layer
            id="ze-draft-fill-outline"
            type="line"
            filter={['==', ['get', 'role'], 'fill']}
            paint={{ 'line-color': TEAL, 'line-width': 2.5 }}
          />
          <Layer
            id="ze-draft-edge-casing"
            type="line"
            filter={['==', ['get', 'role'], 'edge']}
            paint={{ 'line-color': '#ffffff', 'line-width': 6, 'line-opacity': 0.85 }}
          />
          <Layer
            id="ze-draft-edge"
            type="line"
            filter={['==', ['get', 'role'], 'edge']}
            paint={{ 'line-color': TEAL, 'line-width': 3 }}
          />
          <Layer
            id="ze-draft-band"
            type="line"
            filter={['==', ['get', 'role'], 'band']}
            paint={{ 'line-color': '#ffffff', 'line-width': 2, 'line-dasharray': [2, 2] }}
          />
        </Source>

        {/* Draft corners (the first closes the shape) */}
        {draft?.kind === 'polygon' &&
          draft.points.map((p, i) => {
            const canClose = i === 0 && draft.points.length >= 3;
            return (
              <Marker
                key={`d${i}`}
                longitude={p[0]}
                latitude={p[1]}
                onClick={(e) => {
                  e.originalEvent.stopPropagation();
                  if (canClose) dispatch({ type: 'finishDraft' });
                  else if (i === draft.points.length - 1 && draft.points.length >= 3) {
                    dispatch({ type: 'finishDraft' });
                  }
                }}
              >
                <HandleButton
                  className={`ze-draft-pt ${i === 0 ? 'is-first' : ''} ${canClose ? 'can-close' : ''}`}
                  label={canClose ? 'First corner: click to close the shape' : `Corner ${i + 1}`}
                />
              </Marker>
            );
          })}

        {/* Polygon editing handles. During a midpoint drag the corners and dots
            stay those of the saved ring (same keys), so the dragged Marker is
            never remounted mid-gesture. */}
        {editing && polygon && !handlesHidden && (
          <>
            {handleRing.map((p, i) => {
              const active = drag?.kind === 'vertex' && drag.index === i;
              const pos = active ? drag.pos : p;
              return (
                <Marker
                  key={`v${i}`}
                  longitude={pos[0]}
                  latitude={pos[1]}
                  draggable
                  onDragStart={(e) => startDrag('vertex', i, e)}
                  onDrag={dragTo}
                  onDragEnd={endDrag}
                  onClick={(e) => {
                    e.originalEvent.stopPropagation();
                    if (consumeLongPress(e)) return;
                    dispatch({ type: 'select', index: selected === i ? null : i });
                  }}
                >
                  <HandleButton
                    className={`ze-vertex ${selected === i ? 'is-selected' : ''} ${active ? 'is-active' : ''} ${invalid ? 'is-invalid' : ''}`}
                    label={`Corner ${i + 1} of ${handleRing.length}`}
                    title={`Corner ${i + 1}: drag to move${touch ? ', press and hold for options' : ', right-click for options'}`}
                    pressed={selected === i}
                    onContext={(x, y) => {
                      dispatch({ type: 'select', index: i });
                      setMenu({ x, y, index: i });
                    }}
                  />
                </Marker>
              );
            })}
            {edgeMidpoints(handleRing).map((p, i) => {
              const active = drag?.kind === 'mid' && drag.index === i;
              if (drag && !active) return null;
              if (!active && !midpointVisible[i]) return null;
              const pos = active ? drag.pos : p;
              return (
                <Marker
                  key={`m${i}`}
                  longitude={pos[0]}
                  latitude={pos[1]}
                  draggable
                  onDragStart={(e) => startDrag('mid', i, e)}
                  onDrag={dragTo}
                  onDragEnd={endDrag}
                  onClick={(e) => {
                    e.originalEvent.stopPropagation();
                    dispatch({
                      type: 'commit',
                      shape: { kind: 'polygon', ring: insertVertex(handleRing, i, p) },
                      label: 'Add corner',
                    });
                    dispatch({ type: 'select', index: i + 1 });
                  }}
                >
                  <HandleButton
                    className={`ze-mid ${active ? 'is-active' : ''}`}
                    label={`Add a corner between corners ${i + 1} and ${((i + 1) % handleRing.length) + 1}`}
                    title="Drag (or click) to add a corner here"
                  />
                </Marker>
              );
            })}
          </>
        )}

        {/* Move the whole zone */}
        {editing && polygon && drag?.kind !== 'vertex' && drag?.kind !== 'mid' && (
          <MoveHandle
            at={drag?.kind === 'move' ? drag.pos : labelPoint(polygon)}
            label="Move the whole zone"
            onDragStart={(e) => startDrag('move', 0, e)}
            onDrag={dragTo}
            onDragEnd={endDrag}
          />
        )}

        {/* Circle: centre (move) + edge (resize) */}
        {editing && circle && (
          <>
            {drag?.kind !== 'radius' && (
              <MoveHandle
                at={drag?.kind === 'center' ? drag.pos : circle.center}
                label="Circle centre — drag to move the circle"
                onDragStart={(e) => startDrag('center', 0, e)}
                onDrag={dragTo}
                onDragEnd={endDrag}
              />
            )}
            {drag?.kind !== 'center' && (
              <Marker
                longitude={
                  drag?.kind === 'radius'
                    ? drag.pos[0]
                    : destination(circle.center, 90, circle.radiusM)[0]
                }
                latitude={
                  drag?.kind === 'radius'
                    ? drag.pos[1]
                    : destination(circle.center, 90, circle.radiusM)[1]
                }
                draggable
                onDragStart={(e) => startDrag('radius', 0, e)}
                onDrag={dragTo}
                onDragEnd={endDrag}
                onClick={(e) => e.originalEvent.stopPropagation()}
              >
                <HandleButton
                  className={`ze-resize ${drag?.kind === 'radius' ? 'is-active' : ''}`}
                  label={`Circle edge, radius ${formatLength(circle.radiusM)} — drag to resize`}
                />
              </Marker>
            )}
            {drag?.kind !== 'radius' && !drag && (
              <Marker
                longitude={destination(circle.center, 90, circle.radiusM)[0]}
                latitude={destination(circle.center, 90, circle.radiusM)[1]}
                anchor="left"
                offset={[18, 0]}
              >
                <span className="ze-map-label">{formatLength(circle.radiusM)}</span>
              </Marker>
            )}
          </>
        )}

        {/* Snap target */}
        {snap && (
          <Marker longitude={snap.point[0]} latitude={snap.point[1]}>
            <span className={`ze-snap ${snap.kind === 'edge' ? 'is-edge' : ''}`} />
          </Marker>
        )}

        {/* Where the outline crosses itself */}
        {crossings.map((p, i) => (
          <Marker key={`x${i}`} longitude={p[0]} latitude={p[1]}>
            <span className="ze-cross" aria-hidden="true">
              ×
            </span>
          </Marker>
        ))}

        {touchTip && (
          <Marker
            longitude={touchTip.at[0]}
            latitude={touchTip.at[1]}
            anchor="bottom"
            offset={[0, -28]}
          >
            <span className="ze-map-label">{touchTip.text}</span>
          </Marker>
        )}

        {searchPin && (
          <Marker longitude={searchPin[0]} latitude={searchPin[1]} anchor="bottom">
            <span className="ze-pin" aria-hidden="true" />
          </Marker>
        )}
      </LexiStarMap>

      {/* Cursor-side readouts (screen space) */}
      {drawTip && (
        <div
          className="ze-map-label absolute z-[5]"
          style={{ left: drawTip.px[0] + 16, top: drawTip.px[1] + 16 }}
          aria-hidden="true"
        >
          {drawTip.text}
        </div>
      )}
      {snap && pointer && !drawTip && (
        <div
          className="ze-map-label absolute z-[5]"
          style={{ left: pointer.px[0] + 16, top: pointer.px[1] + 16 }}
          aria-hidden="true"
        >
          Snap: {snap.label}
        </div>
      )}
      {hover && hover.name && !drag && (
        <div
          className="ze-map-label absolute z-[5]"
          style={{ left: hover.px[0] + 14, top: hover.px[1] - 30 }}
          role="status"
        >
          {hover.name}
        </div>
      )}

      {/* Corner menu (right-click / press and hold) */}
      {menu && (
        <div
          data-ze-menu
          role="menu"
          aria-label={`Corner ${menu.index + 1}`}
          className="fixed z-[2000] min-w-[180px] rounded-lg border border-gray-200 bg-surface py-1 shadow-xl"
          style={{
            left: Math.min(menu.x, window.innerWidth - 200),
            top: Math.min(menu.y, window.innerHeight - 110),
          }}
        >
          <button
            type="button"
            role="menuitem"
            disabled={vertexCount <= 3}
            onClick={() => {
              dispatch({ type: 'deleteVertex', index: menu.index });
              setMenu(null);
            }}
            className="block w-full px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-400"
          >
            Delete corner {menu.index + 1}
            {vertexCount <= 3 && (
              <span className="block text-xs text-gray-400">A zone needs at least 3 corners</span>
            )}
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => setMenu(null)}
            className="block w-full px-3 py-2 text-left text-sm text-gray-600 hover:bg-gray-100"
          >
            Cancel
          </button>
        </div>
      )}

      {children}
    </div>
  );
});

function MoveHandle({
  at,
  label,
  onDragStart,
  onDrag,
  onDragEnd,
}: {
  at: LngLat;
  label: string;
  onDragStart: (e: MarkerDragEvent) => void;
  onDrag: (e: MarkerDragEvent) => void;
  onDragEnd: () => void;
}) {
  return (
    <Marker
      longitude={at[0]}
      latitude={at[1]}
      draggable
      onDragStart={onDragStart}
      onDrag={onDrag}
      onDragEnd={onDragEnd}
      onClick={(e) => e.originalEvent.stopPropagation()}
    >
      <HandleButton className="ze-move" label={label}>
        <Move size={16} strokeWidth={2.5} aria-hidden="true" />
      </HandleButton>
    </Marker>
  );
}

export default ZoneEditorMap;
